import React, { useCallback, useEffect, useMemo, useState } from "react";
import type { Movement } from "@meupedaco/shared";
import { AutocompleteInput } from "../../components/AutocompleteInput";
import { CrudPage } from "../../components/CrudPage";
import { useAuth } from "../../auth/AuthContext";
import { getCurrentMonthRange } from "../../utils/dateUtils";
import { formatDateBR, formatMoneyBR } from "../../utils/format";
import {
  MOVEMENT_NATURE_PT,
  MOVEMENT_ORIGIN_PT,
  MOVEMENT_TYPE_PT,
} from "../../constants/movementsPt";
import {
  addMonths,
  formatDateInput,
  getMonthRange,
  getYearRange,
} from "../../utils/dateRange";

export const MovementsPage: React.FC = () => {
  const { client } = useAuth();
  type MovementRow = Movement & {
    account?: { id: number; code: string; name: string } | null;
    partnerId?: number | null;
  };
  const currentRange = useMemo(() => getCurrentMonthRange(), []);
  const [startDate, setStartDate] = useState(currentRange.from);
  const [endDate, setEndDate] = useState(currentRange.to);
  const [bankAccountOption, setBankAccountOption] = useState<{
    id: number;
    label: string;
  } | null>(null);
  const [summary, setSummary] = useState<{
    summaryByAccount: Array<{
      bankAccountId: number;
      bankName: string;
      accountLabel: string;
      initialBalance: number;
      entries: number;
      exits: number;
      finalBalance: number;
    }>;
    summaryTotal: {
      initialBalance: number;
      entries: number;
      exits: number;
      finalBalance: number;
    };
  } | null>(null);

  const listFilters = useMemo(
    () => ({
      start: startDate,
      end: endDate,
      bankAccountId: bankAccountOption?.id,
    }),
    [startDate, endDate, bankAccountOption?.id]
  );

  const applyRange = useCallback((range: { from: Date; to: Date }) => {
    setStartDate(formatDateInput(range.from));
    setEndDate(formatDateInput(range.to));
  }, []);

  const handlePrevMonth = useCallback(() => {
    const base = startDate ? new Date(startDate) : new Date();
    const range = getMonthRange(addMonths(base, -1));
    applyRange(range);
  }, [applyRange, startDate]);

  const handleNextMonth = useCallback(() => {
    const base = startDate ? new Date(startDate) : new Date();
    const range = getMonthRange(addMonths(base, 1));
    applyRange(range);
  }, [applyRange, startDate]);

  const handleThisMonth = useCallback(() => {
    const range = getMonthRange(new Date());
    applyRange(range);
  }, [applyRange]);

  const handleFullYear = useCallback(() => {
    const base = startDate ? new Date(startDate) : new Date();
    const range = getYearRange(base);
    applyRange(range);
  }, [applyRange, startDate]);

  const loadSummary = useCallback(async () => {
    const params = new URLSearchParams();
    if (startDate) params.set("start", startDate);
    if (endDate) params.set("end", endDate);
    if (bankAccountOption?.id) params.set("bankAccountId", String(bankAccountOption.id));
    params.set("includeSummary", "true");
    params.set("take", "1");
    params.set("skip", "0");
    const response = await client.get(`/modules/movements?${params.toString()}`);
    if (!response.ok) {
      setSummary(null);
      return;
    }
    const payload = (await response.json()) as {
      summaryByAccount?: Array<{
        bankAccountId: number;
        bankName: string;
        accountLabel: string;
        initialBalance: number;
        entries: number;
        exits: number;
        finalBalance: number;
      }>;
      summaryTotal?: {
        initialBalance: number;
        entries: number;
        exits: number;
        finalBalance: number;
      };
    };
    if (!payload.summaryByAccount) {
      setSummary(null);
      return;
    }
    setSummary({
      summaryByAccount: payload.summaryByAccount,
      summaryTotal: payload.summaryTotal ?? {
        initialBalance: 0,
        entries: 0,
        exits: 0,
        finalBalance: 0,
      },
    });
  }, [bankAccountOption?.id, client, endDate, startDate]);

  useEffect(() => {
    void loadSummary();
  }, [loadSummary]);

  const fetchBankAccounts = useCallback(
    async (query: string) => {
      const response = await client.get(
        `/modules/bank-accounts?q=${encodeURIComponent(query)}&take=10&skip=0`
      );
      if (!response.ok) {
        return [];
      }
      const payload = (await response.json()) as {
        data: Array<{ id: number; bank: { name: string }; agency: string; accountNumber: string }>;
      };
      return payload.data.map((item) => ({
        id: item.id,
        label: `${item.bank.name} ${item.agency}/${item.accountNumber}`,
      }));
    },
    [client]
  );

  const fetchBankAccountOptions = useCallback(async (query: string) => {
    void query;
    const response = await client.get(`/modules/bank-accounts?take=200&skip=0`);
    if (!response.ok) {
      return [];
    }
    const payload = (await response.json()) as {
      data: Array<{ id: number; bank: { name: string }; agency: string; accountNumber: string }>;
    };
    return payload.data.map((item) => ({
      id: item.id,
      label: `${item.bank.name} ${item.agency}/${item.accountNumber}`,
    }));
  }, [client]);

  const fetchAccounts = useCallback(
    async (query: string) => {
      const response = await client.get(
        `/modules/accounts?q=${encodeURIComponent(query)}&take=10&skip=0`
      );
      if (!response.ok) {
        return [];
      }
      const payload = (await response.json()) as {
        data: Array<{ id: number; code: string; name: string }>;
      };
      return payload.data.map((item) => ({
        id: item.id,
        label: `${item.code} - ${item.name}`,
      }));
    },
    [client]
  );

  const fetchCategoryOptions = useCallback(async (query: string) => {
    void query;
    const response = await client.get(`/modules/categories?take=200&skip=0`);
    if (!response.ok) {
      return [];
    }
    const payload = (await response.json()) as {
      data: Array<{ id: number; name: string }>;
    };
    return payload.data.map((item) => ({ id: item.id, label: item.name }));
  }, [client]);

  const fetchCostCenterOptions = useCallback(async (query: string) => {
    void query;
    const response = await client.get(`/modules/cost-centers?take=200&skip=0`);
    if (!response.ok) {
      return [];
    }
    const payload = (await response.json()) as {
      data: Array<{ id: number; code?: string | null; name: string }>;
    };
    return payload.data.map((item) => ({
      id: item.id,
      label: `${item.code ?? ""} ${item.name}`.trim(),
    }));
  }, [client]);

  const toDateInput = useCallback((value: string | number | boolean | null) => {
    if (!value || typeof value !== "string") {
      return "";
    }
    if (value.length >= 10) {
      return value.slice(0, 10);
    }
    return value;
  }, []);

  const MovementFormExtras: React.FC<{
    values: Record<string, string | number | boolean | null>;
    setValues: React.Dispatch<
      React.SetStateAction<Record<string, string | number | boolean | null>>
    >;
    editingId: number | null;
  }> = ({ values, setValues, editingId }) => {
    useEffect(() => {
      if (!editingId) {
        return;
      }
      const normalized = toDateInput(values.occurredAt);
      if (normalized && normalized !== values.occurredAt) {
        setValues((prev) => ({ ...prev, occurredAt: normalized }));
      }
    }, [editingId, setValues, toDateInput, values.occurredAt]);

    return null;
  };

  const isMovementIncomplete = useCallback((movement: MovementRow) => {
    if (!movement.bankAccountId) return true;
    if (!movement.occurredAt) return true;
    if (!movement.amount || Number(movement.amount) === 0) return true;
    if (!movement.description || !movement.description.trim()) return true;
    if (!movement.type) return true;
    if (!movement.operationNature) return true;
    if ((movement.type === "IN" || movement.type === "OUT") && !movement.accountId) {
      return true;
    }
    if (movement.origin === "PAYABLE" && !movement.payableId) return true;
    if (movement.origin === "RECEIVABLE" && !movement.receivableId) return true;
    if (
      movement.origin === "PARTNER" &&
      "partnerId" in movement &&
      !movement.partnerId
    ) {
      return true;
    }
    if (movement.type === "TRANSFER" && !movement.transferGroupId) return true;
    return false;
  }, []);

  return (
    <CrudPage<MovementRow>
      title="Movimentações"
      endpoint="/modules/movements"
      searchPlaceholder="Buscar por descrição ou banco..."
      listFilters={listFilters}
      renderFilters={
        <div
          className="px-3 py-2 mb-2 rounded"
          style={{ background: "#f8f9fa", borderTop: "3px solid #0d6efd" }}
        >
          <small className="text-muted fw-semibold d-block mb-2">Filtros</small>
          <div className="row g-2 align-items-end">
          <div className="col-12 col-md-4">
            <label className="form-label mb-1 small fw-semibold">Conta bancária</label>
            <AutocompleteInput
              placeholder="Buscar conta"
              value={bankAccountOption?.id ?? null}
              selectedLabel={bankAccountOption?.label}
              fetchOptions={fetchBankAccounts}
              onSelect={setBankAccountOption}
            />
          </div>
          <div className="col-12 col-md-3">
            <label className="form-label mb-1 small fw-semibold">Início</label>
            <input
              type="date"
              className="form-control form-control-sm"
              value={startDate}
              onChange={(event) => setStartDate(event.target.value)}
            />
          </div>
          <div className="col-12 col-md-3">
            <label className="form-label mb-1 small fw-semibold">Fim</label>
            <input
              type="date"
              className="form-control form-control-sm"
              value={endDate}
              onChange={(event) => setEndDate(event.target.value)}
            />
          </div>
          <div className="col-12 col-md-2 align-self-end">
            <div className="btn-group btn-group-sm w-100" role="group" aria-label="Período">
              <button type="button" className="btn btn-outline-secondary" onClick={handlePrevMonth} title="Mês anterior">
                <i className="bi bi-chevron-left" />
              </button>
              <button type="button" className="btn btn-outline-primary" onClick={handleThisMonth} title="Mês atual">
                Mês
              </button>
              <button type="button" className="btn btn-outline-primary" onClick={handleFullYear} title="Ano todo">
                Ano
              </button>
              <button type="button" className="btn btn-outline-secondary" onClick={handleNextMonth} title="Próximo mês">
                <i className="bi bi-chevron-right" />
              </button>
            </div>
          </div>
          {summary && (
            <div className="col-12">
              <div className="card border shadow-sm">
                <div className="card-body">
                  <div className="d-flex justify-content-between align-items-center mb-3">
                    <strong>Resumo por conta</strong>
                    <small className="text-muted">
                      Total: {formatMoneyBR(summary.summaryTotal.finalBalance)}
                    </small>
                  </div>
                  <div className="table-responsive">
                    <table className="table table-sm align-middle mb-0">
                      <thead>
                        <tr>
                          <th>Conta</th>
                          <th>Saldo inicial</th>
                          <th>Entradas</th>
                          <th>Saidas</th>
                          <th>Saldo final</th>
                        </tr>
                      </thead>
                      <tbody>
                        {summary.summaryByAccount.map((item) => (
                          <tr key={item.bankAccountId}>
                            <td>
                              <div className="fw-semibold">{item.bankName}</div>
                              <div className="text-muted small">{item.accountLabel}</div>
                            </td>
                            <td>{formatMoneyBR(item.initialBalance)}</td>
                            <td className="text-success">{formatMoneyBR(item.entries)}</td>
                            <td className="text-danger">{formatMoneyBR(item.exits)}</td>
                            <td className={`fw-semibold ${item.finalBalance >= 0 ? "text-success" : "text-danger"}`}>{formatMoneyBR(item.finalBalance)}</td>
                          </tr>
                        ))}
                      </tbody>
                      <tfoot>
                        <tr className="fw-semibold">
                          <td>Total</td>
                          <td>{formatMoneyBR(summary.summaryTotal.initialBalance)}</td>
                          <td className="text-success">{formatMoneyBR(summary.summaryTotal.entries)}</td>
                          <td className="text-danger">{formatMoneyBR(summary.summaryTotal.exits)}</td>
                          <td className={summary.summaryTotal.finalBalance >= 0 ? "text-success" : "text-danger"}>{formatMoneyBR(summary.summaryTotal.finalBalance)}</td>
                        </tr>
                      </tfoot>
                    </table>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
        </div>
      }
      columns={[
        {
          label: "Descrição",
          render: (item) => {
            const incomplete = isMovementIncomplete(item);
            return (
              <div className="d-flex align-items-center gap-1">
                {incomplete && (
                  <span
                    className="badge text-bg-warning text-dark"
                    style={{ fontSize: "0.65rem" }}
                    title="Dados incompletos"
                  >!</span>
                )}
                <span>{item.description || "-"}</span>
              </div>
            );
          },
        },
        {
          label: "Tipo",
          render: (item) => {
            const label = MOVEMENT_TYPE_PT[item.type] ?? item.type;
            const colors: Record<string, string> = {
              IN: "text-bg-success",
              OUT: "text-bg-danger",
              TRANSFER: "text-bg-info text-dark",
            };
            return (
              <span className={`badge ${colors[item.type] ?? "text-bg-secondary"}`} style={{ fontSize: "0.7rem" }}>
                {label}
              </span>
            );
          },
        },
        {
          label: "Origem",
          render: (item) => (
            <span className="text-muted small">{MOVEMENT_ORIGIN_PT[item.origin] ?? item.origin}</span>
          ),
        },
        {
          label: "Natureza",
          render: (item) => {
            const n = item.operationNature ?? "";
            const label = (MOVEMENT_NATURE_PT[n] ?? n) || "-";
            const colors: Record<string, string> = {
              OPEX: "text-bg-primary",
              CAPEX: "text-bg-warning text-dark",
              FINANCE: "text-bg-info",
              OTHER: "text-bg-secondary",
            };
            return (
              <span className={`badge ${colors[n] ?? "text-bg-secondary"}`} style={{ fontSize: "0.7rem" }}>
                {label}
              </span>
            );
          },
        },
        {
          label: "Valor",
          render: (item) => {
            const amount = Number(item.amount);
            const colorClass = item.type === "IN" ? "text-success" : item.type === "OUT" ? "text-danger" : "";
            return <span className={`fw-semibold ${colorClass}`}>{formatMoneyBR(amount)}</span>;
          },
        },
        { label: "Data", render: (item) => formatDateBR(item.occurredAt) },
      ]}
      formFields={[
        {
          name: "bankAccountId",
          label: "Conta bancária",
          type: "select",
          fetchOptions: fetchBankAccountOptions,
        },
        {
          name: "accountId",
          label: "Conta contábil",
          type: "autocomplete",
          placeholder: "Buscar conta contabil",
          fetchOptions: fetchAccounts,
          getIdFromItem: (item) => item.accountId ?? null,
          getLabelFromItem: (item) =>
            item.account ? `${item.account.code} - ${item.account.name}` : "",
        },
        {
          name: "categoryId",
          label: "Categoria",
          type: "select",
          fetchOptions: fetchCategoryOptions,
        },
        {
          name: "costCenterId",
          label: "Centro de custo",
          type: "select",
          fetchOptions: fetchCostCenterOptions,
        },
        {
          name: "type",
          label: "Tipo",
          type: "select",
          disabled: ({ values }) => values.origin !== "MANUAL",
          options: [
            { value: "IN", label: MOVEMENT_TYPE_PT.IN },
            { value: "OUT", label: MOVEMENT_TYPE_PT.OUT },
            { value: "TRANSFER", label: MOVEMENT_TYPE_PT.TRANSFER },
          ],
        },
        {
          name: "origin",
          label: "Origem",
          type: "select",
          disabled: ({ values }) => values.origin !== "MANUAL",
          options: [
            { value: "MANUAL", label: MOVEMENT_ORIGIN_PT.MANUAL },
            { value: "PAYABLE", label: MOVEMENT_ORIGIN_PT.PAYABLE },
            { value: "RECEIVABLE", label: MOVEMENT_ORIGIN_PT.RECEIVABLE },
            { value: "ESTORNO_PAGAMENTO", label: MOVEMENT_ORIGIN_PT.ESTORNO_PAGAMENTO },
            { value: "ESTORNO_RECEBIMENTO", label: MOVEMENT_ORIGIN_PT.ESTORNO_RECEBIMENTO },
            { value: "OPEN_FINANCE", label: MOVEMENT_ORIGIN_PT.OPEN_FINANCE },
            { value: "CREDIT_CARD", label: MOVEMENT_ORIGIN_PT.CREDIT_CARD },
            { value: "PARTNER", label: MOVEMENT_ORIGIN_PT.PARTNER },
            { value: "INITIAL_BALANCE", label: MOVEMENT_ORIGIN_PT.INITIAL_BALANCE },
          ],
        },
        {
          name: "operationNature",
          label: "Natureza",
          type: "select",
          disabled: ({ values }) => values.origin !== "MANUAL",
          options: [
            { value: "OPEX", label: MOVEMENT_NATURE_PT.OPEX },
            { value: "CAPEX", label: MOVEMENT_NATURE_PT.CAPEX },
            { value: "FINANCE", label: MOVEMENT_NATURE_PT.FINANCE },
            { value: "OTHER", label: MOVEMENT_NATURE_PT.OTHER },
          ],
        },
        { name: "description", label: "Descrição", type: "text", required: true },
        {
          name: "amount",
          label: "Valor",
          type: "text",
          required: true,
          disabled: ({ values }) => values.origin !== "MANUAL",
        },
        {
          name: "occurredAt",
          label: "Data",
          type: "date",
          required: true,
          disabled: ({ values }) => values.origin !== "MANUAL",
        },
      ]}
      initialValues={{
        bankAccountId: null,
        accountId: null,
        categoryId: null,
        costCenterId: null,
        type: "IN",
        origin: "MANUAL",
        operationNature: "OTHER",
        description: "",
        amount: "",
        occurredAt: "",
      }}
      mapToPayload={(values) => {
        if (
          (values.type === "IN" || values.type === "OUT") &&
          !values.accountId
        ) {
          throw new Error("Conta contábil é obrigatória para entradas e saídas.");
        }
        return {
          bankAccountId: values.bankAccountId ? Number(values.bankAccountId) : null,
          accountId: values.accountId ? Number(values.accountId) : null,
          categoryId: values.categoryId ? Number(values.categoryId) : null,
          costCenterId: values.costCenterId ? Number(values.costCenterId) : null,
          type: values.type,
          origin: values.origin,
          operationNature: values.operationNature || undefined,
          description: values.description,
          amount: Number(values.amount),
          occurredAt: values.occurredAt,
        };
      }}
      renderFormExtras={({ values, setValues, editingId }) => (
        <MovementFormExtras
          values={values}
          setValues={setValues}
          editingId={editingId}
        />
      )}
      permissionWrite="MOVEMENT_WRITE"
    />
  );
};
